import './recipientsTable.scss';
import { useEffect, useState } from 'react';
import cn from 'classnames';
import Button from 'components/button';
import InputField from 'components/inputField';
import { useNotificationRecipientsQuery } from 'hooks/queries/use-notification.queries';
import { RECIPIENTS_PAGE_LIMIT } from 'services/api/notifications/getNotificationRecipients';
import { formattedPhoneNumber } from 'helpers/formattedPhoneNumber';
import {
	RECIPIENT_STATUSES,
	formatDateTime,
	getRecipientStatusFilterLabel,
	getRecipientStatusGroup,
	getRecipientStatusLabel,
} from 'helpers/notificationDelivery';

const SEARCH_DEBOUNCE_MS = 300;

const getRecipientName = (member) => {
	if (!member) return 'Deleted member';
	const name = `${member.first_name || ''} ${member.last_name || ''}`.trim();
	return name || '—';
};

export const RecipientsTable = ({ notificationId, notification }) => {
	const [status, setStatus] = useState('');
	const [searchInput, setSearchInput] = useState('');
	const [search, setSearch] = useState('');
	const [page, setPage] = useState(1);

	useEffect(() => {
		const timeout = setTimeout(() => {
			setSearch(searchInput.trim());
			setPage(1);
		}, SEARCH_DEBOUNCE_MS);
		return () => clearTimeout(timeout);
	}, [searchInput]);

	const stats = notification?.stats;
	const hasRecipients = stats?.total > 0;

	const { data, isLoading, isFetching, isPlaceholderData, error } =
		useNotificationRecipientsQuery(
			notificationId,
			{ status, search, page, limit: RECIPIENTS_PAGE_LIMIT },
			notification,
		);

	const recipients = data && !data.error ? data.recipients : [];
	const pagination = data && !data.error ? data.pagination : null;
	const totalPages = pagination?.total_pages || 0;

	// Polling can shrink a filtered result: never stay on a page past the end
	useEffect(() => {
		if (totalPages > 0 && page > totalPages) setPage(totalPages);
	}, [page, totalPages]);

	if (!hasRecipients) {
		return (
			<div className="RecipientsTable">
				<h4 className="RecipientsTable__title">Recipients</h4>
				<p className="RecipientsTable__notice">
					Delivery details are not available for notifications sent before
					per-recipient tracking
				</p>
			</div>
		);
	}

	// One option per exact status (the endpoint filters by a single status),
	// only for statuses with recipients, plus the one currently selected.
	const statusOptions = RECIPIENT_STATUSES.filter(
		(option) => stats[option] > 0 || option === status,
	);

	const onChangeStatus = (newStatus) => {
		setStatus(newStatus);
		setPage(1);
	};

	const firstItem = pagination ? (pagination.page - 1) * pagination.limit + 1 : 0;
	const lastItem = pagination
		? Math.min(pagination.page * pagination.limit, pagination.total)
		: 0;

	return (
		<div className="RecipientsTable">
			<h4 className="RecipientsTable__title">Recipients</h4>

			<div className="RecipientsTable__filters">
				<div className="RecipientsTable__chips" role="group" aria-label="Filter by status">
					<button
						type="button"
						className={cn('RecipientsTable__chip', {
							'is-active': status === '',
						})}
						onClick={() => onChangeStatus('')}>
						All <span>{stats.total.toLocaleString()}</span>
					</button>
					{statusOptions.map((option) => (
						<button
							key={option}
							type="button"
							className={cn(
								'RecipientsTable__chip',
								`RecipientsTable__chip--${getRecipientStatusGroup(option)}`,
								{ 'is-active': status === option },
							)}
							onClick={() => onChangeStatus(option)}>
							{getRecipientStatusFilterLabel(option)}{' '}
							<span>{stats[option].toLocaleString()}</span>
						</button>
					))}
				</div>
				<div className="RecipientsTable__search">
					<InputField
						id="recipientsSearch"
						name="recipientsSearch"
						type="search"
						placeholder="Search by name or phone"
						value={searchInput}
						onChange={(e) => setSearchInput(e.target.value)}
					/>
				</div>
			</div>

			{error || data?.error ? (
				<p className="RecipientsTable__notice">
					{data?.data?.response_description || 'Error loading recipients'}
				</p>
			) : isLoading ? (
				<p className="RecipientsTable__notice">Loading recipients…</p>
			) : recipients.length === 0 ? (
				<p className="RecipientsTable__notice">
					No recipients for the selected filters
				</p>
			) : (
				<>
					<div
						className={cn('RecipientsTable__wrapper', {
							'is-fetching': isFetching && isPlaceholderData,
						})}>
						<table className="RecipientsTable__table">
							<thead>
								<tr>
									<th>Name</th>
									<th>Phone</th>
									<th>Status</th>
									<th>Error</th>
									<th>Sent</th>
									<th>Last update</th>
								</tr>
							</thead>
							<tbody>
								{recipients.map((recipient) => (
									<tr key={recipient.id}>
										<td
											className={cn({
												'RecipientsTable__muted': !recipient.member,
											})}>
											{getRecipientName(recipient.member)}
										</td>
										<td className="RecipientsTable__nowrap">
											{recipient.phone_number
												? formattedPhoneNumber(recipient.phone_number)
												: '—'}
										</td>
										<td>
											<span
												className={cn(
													'RecipientsTable__status',
													`RecipientsTable__status--${getRecipientStatusGroup(
														recipient.status,
													)}`,
												)}>
												{getRecipientStatusLabel(recipient.status)}
											</span>
										</td>
										<td className="RecipientsTable__error">
											{recipient.error_message || '—'}
										</td>
										<td className="RecipientsTable__nowrap">
											{formatDateTime(recipient.sent_at) || '—'}
										</td>
										<td className="RecipientsTable__nowrap">
											{formatDateTime(recipient.status_at) || '—'}
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>

					{pagination && (
						<div className="RecipientsTable__pagination">
							<span>
								{firstItem.toLocaleString()}–{lastItem.toLocaleString()} of{' '}
								{pagination.total.toLocaleString()}
							</span>
							<div className="RecipientsTable__pagination__controls">
								<Button
									buttonWrapperClass="RecipientsTable__pageButton"
									displayLabel="Previous"
									type="button"
									disabled={page <= 1}
									onClick={() => setPage((current) => current - 1)}
								/>
								<span>
									Page {page} of {Math.max(totalPages, 1)}
								</span>
								<Button
									buttonWrapperClass="RecipientsTable__pageButton"
									displayLabel="Next"
									type="button"
									// Wait for the current page before moving on, so a
									// double click does not skip a page
									disabled={page >= totalPages || isPlaceholderData}
									onClick={() => setPage((current) => current + 1)}
								/>
							</div>
						</div>
					)}
				</>
			)}
		</div>
	);
};
